import { TenantAttributeType, TenantAttributeTypeI18N, type TenantAttributeSpec } from "@the-real-insight/tri-model";

import type { DinAttr, DinNode, DinTree } from "../../../administration/din276/din276Tree";

/**
 * The customer's data model on top of the DIN 276: which classes a filter finds, and the attributes
 * the customer added to them, as rows.
 *
 * Pure — the tabs render what these return, and the tests pin the rules without a DOM.
 */

/** A customer attribute as the server stores it. */
export interface TenantAttribute {
  _id: string;
  classCode: string;
  name: string;
  valueType: TenantAttributeType;
  unit?: string | null;
  allowedValues: string[];
  /** The fields of a structure, recursively. */
  fields?: TenantAttributeSpec[];
  updatedBy?: string;
  updatedAt?: string;
}

export interface ClassFilter {
  /** Matched against the class name and its comments — the comments are where alternative names live. */
  text: string;
  /** The DIN 276 code or its beginning: "44" finds 440, 441, 444.1 … */
  code: string;
  /** Show the K. / T. / F. types of the ontology layer. Off, as in the administration. */
  showSynthetic: boolean;
  /** Show provisional nodes the pipeline has not confirmed yet. Off, as in the administration. */
  showPending: boolean;
}

export const EMPTY_FILTER: ClassFilter = { text: "", code: "", showSynthetic: false, showPending: false };

const SYNTHETIC_KINDS = new Set(["component", "locus", "funktion"]);

const normalize = (value: string) => value.toLocaleLowerCase("de").trim();

export const isFilterActive = (filter: ClassFilter): boolean => !!filter.text.trim() || !!filter.code.trim();

/** Whether a class is shown at all — the two switches of the administration tree. */
const isVisible = (node: DinNode, filter: ClassFilter): boolean =>
  (filter.showSynthetic || !SYNTHETIC_KINDS.has(node.kind)) && (filter.showPending || !node.synthetic);

/**
 * The classes a filter finds, in code order.
 *
 * Every word of the text has to occur in the name or the comments; the code matches from its
 * beginning, so a reader can narrow from a cost group down to one position.
 */
export const matchClasses = (tree: DinTree, filter: ClassFilter): DinNode[] => {
  const words = normalize(filter.text).split(/\s+/).filter(Boolean);
  const code = filter.code.trim();

  return Object.values(tree.byCode)
    .filter((node) => isVisible(node, filter))
    .filter((node) => !code || node.code.startsWith(code))
    .filter((node) => {
      if (words.length === 0) return true;
      const haystack = normalize(`${node.label} ${node.comments ?? ""}`);
      return words.every((word) => haystack.includes(word));
    })
    .sort((a, b) => codeKey(a.code).localeCompare(codeKey(b.code)));
};

const codeKey = (code: string) =>
  code
    .split(".")
    .map((part) => part.padStart(4, "0"))
    .join(".");

/**
 * What the tree shows for a filter: the hits and the path down to each of them.
 *
 * The path is shown so a hit stands where it belongs — "Brandschutztür" under Türen under Innenwände
 * — and is not a leaf floating on its own. Only the hits are what an added attribute applies to.
 */
export const visibleInTree = (tree: DinTree, hits: DinNode[]): Set<string> => {
  const visible = new Set<string>();
  hits.forEach((node) => {
    let code: string | undefined = node.code;
    while (code && !visible.has(code)) {
      visible.add(code);
      code = tree.parentOf[code];
    }
  });
  return visible;
};

/**
 * Whether a class stands outside the classification tree — the K. / T. / F. types, which carry no
 * level. The tree cannot reach them from its roots, so a view that shows hits has to list them
 * on their own.
 */
export const isOutsideTree = (node: DinNode): boolean => node.level == null;

/** A customer attribute in the form the DIN 276 datasheet shows its own. */
export const toDinAttr = (
  attribute: Pick<TenantAttribute, "name" | "valueType" | "unit" | "allowedValues" | "fields">,
  lang: "de" | "en" = "de",
): DinAttr => ({
  name: attribute.name,
  type: TenantAttributeTypeI18N[attribute.valueType]?.[lang] ?? attribute.valueType,
  unit: attribute.unit ?? null,
  enum: attribute.allowedValues ?? [],
  measured: false,
  typeUnclear: false,
  children: attribute.fields?.map((field) => toDinAttr(field, lang)),
});

/** How many fields a structure holds, all levels counted — for a one-line summary. */
export const countFields = (fields: TenantAttributeSpec[] | undefined): number =>
  (fields ?? []).reduce((sum, field) => sum + 1 + countFields(field.fields), 0);

/** "Firma, Laufzeit, Ansprechpartner (Name, Telefon)" — the shape of a structure on one line. */
export const describeFields = (fields: TenantAttributeSpec[] | undefined): string =>
  (fields ?? [])
    .map((field) => (field.fields?.length ? `${field.name} (${describeFields(field.fields)})` : field.name))
    .join(", ");

export interface AttributeRow extends TenantAttribute {
  className: string;
}

export interface AttributeFilter {
  className: string;
  code: string;
  name: string;
}

export const EMPTY_ATTRIBUTE_FILTER: AttributeFilter = { className: "", code: "", name: "" };

/**
 * The customer attributes as rows, one per class and attribute, filtered.
 *
 * Class name and attribute name are matched as text anywhere, the code from its beginning — the
 * same way the class filter reads a code.
 */
export const attributeRows = (
  attributes: TenantAttribute[],
  tree: DinTree | null,
  filter: AttributeFilter,
): AttributeRow[] => {
  const className = normalize(filter.className);
  const name = normalize(filter.name);
  const code = filter.code.trim();

  return attributes
    .map((attribute) => ({ ...attribute, className: tree?.byCode[attribute.classCode]?.label ?? attribute.classCode }))
    .filter((row) => !code || row.classCode.startsWith(code))
    .filter((row) => !className || normalize(row.className).includes(className))
    .filter((row) => !name || normalize(row.name).includes(name))
    .sort(
      (a, b) =>
        codeKey(a.classCode).localeCompare(codeKey(b.classCode)) || a.name.localeCompare(b.name, "de"),
    );
};

/**
 * The one attribute name all rows share, or nothing.
 *
 * Only then can the rows be changed as a whole: a set of one attribute across many classes. Compared
 * the way the server compares — without case.
 */
export const sharedName = (rows: TenantAttribute[]): string | null => {
  if (rows.length === 0) return null;
  const first = normalize(rows[0].name);
  return rows.every((row) => normalize(row.name) === first) ? rows[0].name : null;
};

/** The customer attributes per class code, for the datasheet of the tree. */
export const attributesByClass = (attributes: TenantAttribute[]): Map<string, TenantAttribute[]> => {
  const byClass = new Map<string, TenantAttribute[]>();
  attributes.forEach((attribute) => {
    byClass.set(attribute.classCode, [...(byClass.get(attribute.classCode) ?? []), attribute]);
  });
  return byClass;
};
